import { Typography } from "antd"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useState } from "react"

import Corgi from "#/assets/corgi.gif"
import Flex from "#blocks/Flex"

interface Heart {
  id: number
  angle: number
  distance: number
  originX: number
  originY: number
}

interface CorgiPopupProps {
  title?: string
  children?: ReactNode
}
const CorgiPopup = ({ title, children }: CorgiPopupProps) => {
  const [hearts, setHearts] = useState<Heart[]>([])
  const [heartCounter, setHeartCounter] = useState(0)

  const handleBellyRub = (event: React.MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const clickY = event.clientY - rect.top

    const newHearts: Heart[] = []

    // Arc from 7 o'clock (210°) to 2 o'clock (60°) = 150° total
    // Divide into 3 sections of 50° each
    const sections = [
      { start: 60, end: 110 }, // 2 o'clock to 4 o'clock
      { start: 110, end: 160 }, // 4 o'clock to 6 o'clock
      { start: 160, end: 210 }, // 6 o'clock to 7 o'clock
    ]

    for (let i = 0; i < 3; i++) {
      const section = sections[i]
      const randomAngle =
        section.start + Math.random() * (section.end - section.start)

      const heartId = heartCounter + i
      newHearts.push({
        id: heartId,
        angle: randomAngle,
        distance: 100 + Math.random() * 20, // 100-120px distance
        originX: clickX,
        originY: clickY,
      })

      // Remove this specific heart after its animation completes
      setTimeout(() => {
        setHearts((prev) => prev.filter((h) => h.id !== heartId))
      }, 1600) // Match the animation duration
    }

    setHearts((prev) => [...prev, ...newHearts])
    setHeartCounter((prev) => prev + 3)
  }
  return (
    <Flex
      style={{
        maxWidth: 800,
        padding: 40,
      }}
      gap={40}
    >
      <div style={{ position: "relative", flex: 1 }}>
        <motion.img
          src={Corgi}
          alt="Empty page"
          onClick={handleBellyRub}
          draggable={false}
          whileHover={{
            scale: 1.02,
            boxShadow: `
                  0 4px 8px rgba(0, 0, 0, 0.12),
                  0 8px 16px rgba(0, 0, 0, 0.08),
                  0 16px 32px rgba(0, 0, 0, 0.04)
                `,
          }}
          whileTap={{
            scale: 0.98,
            boxShadow: `
                  0 1px 2px rgba(0, 0, 0, 0.1),
                  0 2px 4px rgba(0, 0, 0, 0.06),
                  0 4px 8px rgba(0, 0, 0, 0.03)
                `,
          }}
          style={{
            borderRadius: 16,
            boxShadow: `
                  0 2px 4px rgba(0, 0, 0, 0.08),
                  0 4px 8px rgba(0, 0, 0, 0.05),
                  0 8px 16px rgba(0, 0, 0, 0.02)
                `,
            border: "2px solid rgba(255, 255, 255, 0.8)",
            cursor: "pointer",
            transform: "translateY(-2px)",
            width: "auto",
            height: "auto",
            maxWidth: "none",
            maxHeight: "none",
            userSelect: "none",
          }}
        />

        <AnimatePresence>
          {hearts.map((heart) => {
            const angleInRadians = (heart.angle * Math.PI) / 180
            const endX = Math.cos(angleInRadians) * heart.distance
            const endY = -Math.sin(angleInRadians) * heart.distance // Flip Y for upward direction

            return (
              <motion.div
                key={heart.id}
                initial={{
                  opacity: 0,
                  scale: 0,
                  x: heart.originX, // Use click position
                  y: heart.originY, // Use click position
                }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1.2, 1, 0.6],
                  x: [heart.originX, heart.originX + endX],
                  y: [heart.originY, heart.originY + endY],
                  rotate: [0, 20, -15, 10],
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{
                  duration: 1.6,
                  ease: [0.25, 0.46, 0.45, 0.94], // Deceleration curve
                  times: [0, 0.3, 0.7, 1],
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  fontSize: "24px",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                💖
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
      <Flex col style={{ width: 450, gap: 16 }}>
        <Typography.Title
          level={4}
          style={{
            color: "#b05412",
            textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
            fontFamily: "Comic Sans MS, cursive, sans-serif",
            margin: 0,
          }}
        >
          {title}
        </Typography.Title>
        {typeof children === "string" ? (
          <Typography.Text
            style={{
              color: "#8B4513",
              textShadow: "1px 1px 3px rgba(0,0,0,0.2)",
              fontFamily: "Comic Sans MS, cursive, sans-serif",
              fontWeight: "normal",
              transform: "rotate(0.5deg)",
              lineHeight: "1.4",
              margin: 0,
            }}
          >
            {children}
          </Typography.Text>
        ) : (
          children
        )}
      </Flex>
    </Flex>
  )
}

export default CorgiPopup
